import React, { useState } from "react";
import { toast } from "sonner";
import { Film, Image as ImageIcon } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import StatusChip from "../StatusChip";
import { SignedMedia } from "./SignedMedia";
import { AiDraftPanel } from "./AiDraftPanel";
import { errMsg } from "../../lib/portfolio";

export function MediaCard({ media, founder, isCover, onChanged }) {
  const [caption, setCaption] = useState(media.caption || "");
  const [alt, setAlt] = useState(media.alt_text || "");
  const [showAi, setShowAi] = useState(false);
  const call = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const Kind = media.kind === "video" ? Film : ImageIcon;
  return (
    <div className="ds-card rounded-lg border border-erf-borderCream overflow-hidden" data-testid={`portfolio-media-card-${media.id}`}>
      <SignedMedia media={media} variant={media.kind === "video" ? "poster" : "web"} className="w-full aspect-[4/3]"
        testId={`portfolio-media-preview-${media.id}`} />
      <div className="p-3 space-y-2">
        <div className="ds-card-header">
          <div className="ds-card-header__title flex items-center gap-1.5 text-sm font-nav text-erf-dark">
            <Kind className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span className="ds-text-safe">{media.kind === "video" ? "Video" : "Photo"}{isCover ? " · Cover" : ""}</span>
          </div>
          <div className="ds-card-header__chip">
            <StatusChip status={media.processing_state} testId={`portfolio-media-state-${media.id}`} />
          </div>
        </div>
        {media.last_error && <p className="text-xs font-nav text-[#991B1B]" role="alert">{media.last_error}</p>}
        {media.duplicate_of?.length > 0 && <p className="text-xs font-nav text-[#78350F]">Same file was uploaded before.</p>}
        {founder ? (
          <>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption" maxLength={400}
              aria-label="Caption" className="min-h-[44px] bg-white text-erf-dark" data-testid={`portfolio-media-caption-${media.id}`} />
            <Input value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Alt text (describe what is visible)" maxLength={280}
              aria-label="Alt text" className="min-h-[44px] bg-white text-erf-dark" data-testid={`portfolio-media-alt-${media.id}`} />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="border-erf-borderCream text-erf-dark" data-testid={`portfolio-media-save-${media.id}`}
                onClick={() => call(() => api.patch(`/admin/portfolio/media/${media.id}`, { caption, alt_text: alt }), "Saved.")}>Save</Button>
              {!isCover && media.processing_state === "ready" && (
                <Button variant="outline" className="border-erf-borderCream text-erf-dark" data-testid={`portfolio-media-cover-${media.id}`}
                  onClick={() => call(() => api.patch(`/admin/portfolio/media/${media.id}`, { is_cover: true }), "Cover set.")}>Set as cover</Button>
              )}
              {["failed", "ready"].includes(media.processing_state) && (
                <Button variant="outline" className="border-erf-borderCream text-erf-dark" data-testid={`portfolio-media-retry-${media.id}`}
                  onClick={() => call(() => api.post(`/admin/portfolio/media/${media.id}/derivatives`), "Processing restarted.")}>
                  {media.processing_state === "failed" ? "Retry processing" : "Re-run processing"}
                </Button>
              )}
              <Button variant="outline" className="border-[#FECACA] text-[#991B1B]" data-testid={`portfolio-media-remove-${media.id}`}
                onClick={() => window.confirm("Remove this file from the project? The private original is kept.") &&
                  call(() => api.delete(`/admin/portfolio/media/${media.id}`), "Removed.")}>Remove</Button>
            </div>
          </>
        ) : (
          <p className="text-sm font-nav text-erf-dark">{media.caption || "No caption yet."}</p>
        )}
        <Button variant="ghost" className="text-erf-dark" aria-expanded={showAi} onClick={() => setShowAi((s) => !s)}
          data-testid={`portfolio-media-ai-toggle-${media.id}`}>
          {showAi ? "Hide AI drafts" : "AI drafts"}
        </Button>
        {showAi && <AiDraftPanel media={media} founder={founder} onChanged={onChanged} />}
      </div>
    </div>
  );
}
